/**
 * Feature Spotlight — ein einzelnes Feature premium erklären (Motion-Graphic).
 *
 * Aufbau: zentrales Glas-Medaillon mit großem Lucide-Icon, umgeben von zwei
 * konzentrischen Ringen. Auf dem äußeren Ring orbitieren zwei leuchtende
 * Punkte (kontinuierliche Rotation). Darunter Eyebrow → Headline (Feature-
 * Name) → Subtext und bis zu drei einkaskadierende Benefit-Pills.
 *
 * Motion:
 *   - Glow + Ringe blenden auf, das Medaillon poppt (back.out), Icon poppt
 *     leicht verzögert.
 *   - Orbit-Container rotiert dauerhaft (repeat:-1) → Punkte kreisen.
 *   - Innerer Ring atmet (scale/opacity yoyo).
 *   - Eyebrow/Headline/Subtext sliden gestaffelt hoch, Pills kaskadieren.
 *   - Dezentes Dauer-Leben: Medaillon-Float.
 *
 * Inputs:
 *   - text            → Headline / Feature-Name (Pflicht), \n = Zeilenumbruch
 *   - subtext         → Erklärzeile unter der Headline
 *   - items           → bis zu 3 Benefit-Pills ("Label" oder "Label | iconKey")
 *   - extras.eyebrow  → Uppercase-Label über der Headline (Default „FEATURE")
 *   - extras.icon     → Haupt-Icon im Medaillon (Default „sparkles"→„zap")
 *   - accent          → Akzent-Token (primary | secondary | accent)
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, gsapSetLine,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";
import { lucideIcon, splitItem } from "../visual/_mockHelpers.js";

export const featureSpotlight: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "FEATURE";
  const iconKey = (inputs.extras?.icon != null ? String(inputs.extras.icon) : "zap").trim() || "zap";
  const headlineRaw = inputs.text ?? "Dein Feature.";
  const headlineLines = headlineRaw.split(/\\n|\n/).map((l) => l.trim()).filter(Boolean);
  const subtext = inputs.subtext ?? "";

  const palette = [accent, themeColors.secondary, themeColors.accent];
  const pills = (inputs.items ?? []).slice(0, 3).map((raw, i) => {
    const [label, iconRaw] = splitItem(raw);
    return {
      label: (label ?? "").trim(),
      icon: (iconRaw ?? "").trim() || "check",
      color: palette[i % palette.length] ?? accent,
    };
  }).filter((p) => p.label);

  // Geometrie aus Format.
  const disc = isVertical ? 360 : 300;
  const iconBox = Math.round(disc * 0.4);
  const ringInner = Math.round(disc * 1.18);
  const ringOuter = Math.round(disc * 1.5);
  const dotSize = Math.round(disc * 0.07);

  const eyebrowPx = typeSize("sm", ctx.format) * 0.62;
  const headlinePx = typeSize("lg", ctx.format) * (isVertical ? 1.0 : 0.95);
  const subPx = typeSize("md", ctx.format) * 0.74;
  const pillPx = typeSize("sm", ctx.format) * 0.74;
  const pillIcon = Math.round(pillPx * 1.15);

  const wrapId = `${clipPrefix}-wrap`;
  const stageId = `${clipPrefix}-stage`;
  const glowId = `${clipPrefix}-glow`;
  const ring1Id = `${clipPrefix}-ring1`;
  const ring2Id = `${clipPrefix}-ring2`;
  const orbitId = `${clipPrefix}-orbit`;
  const discId = `${clipPrefix}-disc`;
  const iconId = `${clipPrefix}-icon`;
  const eyebrowId = `${clipPrefix}-eyebrow`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const pillIds = pills.map((_, i) => `${clipPrefix}-pill-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  // Orbit: zwei gegenüberliegende, leuchtende Punkte auf dem äußeren Ring.
  function orbitDot(top: boolean): string {
    const offset = ringOuter / 2;
    const c = top ? accent : themeColors.accent;
    return `<div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%) rotate(${top ? 0 : 180}deg) translateY(-${offset}px);">
      <div style="width:${dotSize}px;height:${dotSize}px;border-radius:9999px;background:${c};box-shadow:0 0 18px ${withAlpha(c, 0.95)}, 0 0 38px ${withAlpha(c, 0.6)};"></div>
    </div>`;
  }

  const headHtml = headlineLines
    .map((line) => `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headlinePx}px;line-height:1.02;letter-spacing:-1.5px;color:${themeColors.fg};text-wrap:balance;">${escapeHtml(line)}</div>`)
    .join("");

  const pillsHtml = pills.length
    ? `<div style="display:flex;flex-wrap:wrap;justify-content:center;gap:${isVertical ? 14 : 12}px;max-width:${isVertical ? 900 : 1100}px;">
        ${pills.map((p, i) => `<div id="${pillIds[i]}" style="display:inline-flex;align-items:center;gap:10px;padding:${isVertical ? "14px 22px" : "12px 20px"};border-radius:9999px;background:${withAlpha(p.color, 0.14)};border:1px solid ${withAlpha(p.color, 0.5)};box-shadow:0 10px 30px rgba(0,0,0,0.3), inset 0 1px 0 ${withAlpha(themeColors.fg, 0.1)};backdrop-filter:blur(6px);color:${themeColors.fg};font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${pillPx}px;letter-spacing:0.2px;white-space:nowrap;">
            <span style="color:${p.color};display:inline-flex;">${lucideIcon(p.icon, pillIcon, p.color, 2.4)}</span>
            ${escapeHtml(p.label)}
          </div>`).join("")}
      </div>`
    : "";

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 56 : 40}px;padding:${isVertical ? "110px 70px" : "70px 110px"};overflow:hidden;">
    <div id="${stageId}" style="position:relative;width:${ringOuter}px;height:${ringOuter}px;flex-shrink:0;">
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${glowId}" style="width:${Math.round(ringOuter * 1.05)}px;height:${Math.round(ringOuter * 1.05)}px;border-radius:9999px;background:radial-gradient(circle, ${withAlpha(accent, 0.34)} 0%, ${withAlpha(accent, 0.08)} 48%, ${withAlpha(accent, 0.0)} 72%);filter:blur(8px);will-change:transform,opacity;"></div>
      </div>
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${ring1Id}" style="width:${ringOuter}px;height:${ringOuter}px;border-radius:9999px;border:1.5px solid ${withAlpha(themeColors.fg, 0.16)};box-shadow:inset 0 0 50px ${withAlpha(accent, 0.12)};will-change:transform,opacity;"></div>
      </div>
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${ring2Id}" style="width:${ringInner}px;height:${ringInner}px;border-radius:9999px;border:2px solid ${withAlpha(accent, 0.4)};will-change:transform,opacity;"></div>
      </div>
      <div id="${orbitId}" style="position:absolute;inset:0;will-change:transform;">
        ${orbitDot(true)}
        ${orbitDot(false)}
      </div>
      <div style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);">
        <div id="${discId}" style="width:${disc}px;height:${disc}px;border-radius:9999px;display:flex;align-items:center;justify-content:center;background:radial-gradient(circle at 36% 28%, ${withAlpha(themeColors.fg, 0.16)} 0%, ${withAlpha(themeColors.bg, 0.84)} 70%);border:1px solid ${withAlpha(themeColors.fg, 0.2)};box-shadow:0 30px 80px rgba(0,0,0,0.5), 0 0 70px ${withAlpha(accent, 0.4)}, inset 0 1px 0 ${withAlpha(themeColors.fg, 0.18)};backdrop-filter:blur(10px);will-change:transform,opacity;">
          <div id="${iconId}" style="color:${accent};display:inline-flex;filter:drop-shadow(0 6px 18px ${withAlpha(accent, 0.6)});will-change:transform,opacity;">${lucideIcon(iconKey, iconBox, accent, 2.1)}</div>
        </div>
      </div>
    </div>
    <div style="display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 16 : 12}px;text-align:center;">
      ${eyebrow ? `<div id="${eyebrowId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyebrowPx}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(eyebrow)}</div>` : ""}
      <div id="${headId}" style="display:flex;flex-direction:column;gap:2px;align-items:center;">${headHtml}</div>
      ${subtext ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.regular};font-size:${subPx}px;line-height:1.32;letter-spacing:-0.4px;color:${withAlpha(themeColors.fg, 0.82)};max-width:${isVertical ? 820 : 1000}px;text-wrap:balance;">${escapeHtml(subtext)}</div>` : ""}
    </div>
    ${pillsHtml}
  </div>`;

  const init = [
    gsapSetLine(`#${glowId}`, { opacity: 0, scale: 0.8, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${ring1Id}`, { opacity: 0, scale: 0.85, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${ring2Id}`, { opacity: 0, scale: 0.85, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${orbitId}`, { opacity: 0, rotation: -90, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${discId}`, { opacity: 0, scale: 0.6, transformOrigin: "50% 50%" }),
    gsapSetLine(`#${iconId}`, { opacity: 0, scale: 0.4, transformOrigin: "50% 50%" }),
    eyebrow ? gsapSetLine(`#${eyebrowId}`, { opacity: 0, y: 14 }) : "",
    gsapSetLine(`#${headId}`, { opacity: 0, y: 18 }),
    subtext ? gsapSetLine(`#${subId}`, { opacity: 0, y: 14 }) : "",
    ...pillIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 18, scale: 0.9 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];

  // 1) Tiefe: Glow + Ringe.
  tweens.push(t.to(`#${glowId}`, { opacity: 1, scale: 1, duration: 0.8, ease: "power2.out" }, 0.1));
  tweens.push(t.to(`#${ring1Id}`, { opacity: 1, scale: 1, duration: 0.7, ease: "power3.out" }, 0.15));
  tweens.push(t.to(`#${ring2Id}`, { opacity: 1, scale: 1, duration: 0.7, ease: "power3.out" }, 0.22));

  // 2) Medaillon poppt, dann Icon.
  tweens.push(t.to(`#${discId}`, { opacity: 1, scale: 1, duration: 0.6, ease: "back.out(1.8)" }, 0.3));
  tweens.push(t.to(`#${iconId}`, { opacity: 1, scale: 1, duration: 0.55, ease: "back.out(2.2)" }, 0.5));

  // 3) Orbit erscheint + dreht dauerhaft.
  tweens.push(t.to(`#${orbitId}`, { opacity: 1, rotation: 0, duration: 0.8, ease: "power2.out" }, 0.45));
  tweens.push(t.to(`#${orbitId}`, { rotation: 360, duration: 9, ease: "none", repeat: -1 }, 1.1));

  // 4) Text staffelt hoch.
  let at = 0.7;
  if (eyebrow) { tweens.push(t.to(`#${eyebrowId}`, { opacity: 1, y: 0, duration: 0.45, ease: "power2.out" }, at)); at += 0.12; }
  tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" }, at)); at += 0.16;
  if (subtext) { tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, at)); at += 0.14; }

  // 5) Pills kaskadieren.
  pillIds.forEach((id, i) => {
    tweens.push(t.to(`#${id}`, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "back.out(1.7)" }, at + 0.1 + i * 0.13));
  });

  // 6) Dezentes Dauer-Leben: Ring2 atmet, Medaillon floatet.
  const lifeAt = Math.min(at + 0.8, Math.max(0.4, clipDurationSec - 0.6));
  tweens.push(t.to(`#${ring2Id}`, { scale: 1.05, opacity: 0.7, duration: 2.4, ease: "sine.inOut", repeat: -1, yoyo: true }, lifeAt));
  tweens.push(t.to(`#${discId}`, { y: isVertical ? -10 : -8, duration: 2.8, ease: "sine.inOut", repeat: -1, yoyo: true }, lifeAt));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
